import type { SupervisorOrgUser } from "../supervisorOrg/index.js";

/** `orgUsers` must be read at decision time: a supervisor demoted or removed after approving no longer counts. */
export const hasSupervisorApproval = (
  approvalUids: readonly string[],
  orgUsers: readonly Pick<SupervisorOrgUser, "uid" | "role">[],
) =>
  orgUsers.some((orgUser) => orgUser.role === "supervisor" && approvalUids.includes(orgUser.uid));

/** `account.externalApprovers` can keep the uid of a user deleted from the org, who can never approve. */
export const requiredExternalApprovers = (
  externalApprovers: readonly string[],
  orgUsers: readonly Pick<SupervisorOrgUser, "uid" | "role">[],
) =>
  externalApprovers.filter((uid) =>
    orgUsers.some((orgUser) => orgUser.uid === uid && orgUser.role === "externalApprover"),
  );

export const isTransferRequestAccepted = ({
  approvalUids,
  externalApprovers,
  orgUsers,
}: {
  approvalUids: readonly string[];
  externalApprovers: readonly string[];
  orgUsers: readonly Pick<SupervisorOrgUser, "uid" | "role">[];
}) =>
  requiredExternalApprovers(externalApprovers, orgUsers).every((uid) =>
    approvalUids.includes(uid),
  ) && hasSupervisorApproval(approvalUids, orgUsers);

/**
 * Whether a review targets the stored version of a transfer request. `reviewedEditedAt` is the
 * `editedAt` the reviewer saw, in milliseconds, `null` or absent if they saw no edit. The client
 * SDK keeps a fractional millisecond that the Admin SDK floors.
 */
export const isReviewedTransferRequestVersion = ({
  storedEditedAt,
  reviewedEditedAt,
}: {
  storedEditedAt: { toMillis: () => number } | undefined;
  reviewedEditedAt: number | null | undefined;
}) =>
  (storedEditedAt?.toMillis() ?? null) ===
  (reviewedEditedAt === undefined || reviewedEditedAt === null
    ? null
    : Math.floor(reviewedEditedAt));
